import { create } from 'zustand'
import type { AgentStatus } from '@kc3/types'
import { getSocket } from '../socket/client.js'

/**
 * Send an agent status transition to the gateway.
 *
 * Wrapped so the store has ONE place that talks to the socket, and so the
 * import stays one-directional: `socket/client.ts` does not import this store,
 * so there is no cycle.
 */
function emitAgentStatus(data: { event: 'pause' | 'unpause'; reason?: string }): void {
  getSocket().emit('agent:status', data)
}

export interface BreakCode {
  id: string
  name: string
  description?: string | null
  alertTimer?: number | null
}

export interface UserStatusOption {
  key: string
  text: string
  disabled?: boolean
  alertTimer?: number | null
}

export interface SessionHistory {
  key: string
  text: string
  time: Date
  activity: string
  duration: number // seconds spent in the PREVIOUS status before this change
  lastStatus?: string | undefined
  // KC2-style accumulated times (in seconds)
  readyTime: number
  availableTime: number
  breakTime: number
}

export type UserState = 'Idle' | 'Waiting'

interface AgentStoreState {
  // Core status
  status: AgentStatus | null
  since: string | null
  currentCallId: string | null
  
  // User status dropdown (Just Logged In, Available, Break Codes)
  userStatus: UserStatusOption
  userState: UserState
  lastUserState: UserState | null
  
  // Session history tracking - matches KC2: Date, Status, Activity, Duration
  sessionHistory: SessionHistory[]
  
  // Break codes
  breakCodes: UserStatusOption[]
  
  // Actions
  setStatus: (status: AgentStatus, since?: string) => void
  setCurrentCall: (callId: string | null) => void
  setUserStatus: (status: UserStatusOption) => void
  setUserState: (state: UserState) => void
  setBreakCodes: (codes: BreakCode[]) => void
  loadSessionHistory: (records: SessionHistory[]) => void
  logout: (forcefully?: boolean) => void
  reset: () => void
}

const defaultBreakCodes: UserStatusOption[] = [
  { key: 'login', text: 'Just Logged In', disabled: false },
  { key: 'available', text: 'Available', disabled: false },
]

const initialState = {
  status: null,
  since: null,
  currentCallId: null,
  userStatus: { key: 'login', text: 'Just Logged In' },
  userState: 'Idle' as UserState,
  lastUserState: null,
  sessionHistory: [],
  breakCodes: defaultBreakCodes,
} satisfies Pick<AgentStoreState, 'status' | 'since' | 'currentCallId' | 'userStatus' | 'userState' | 'lastUserState' | 'sessionHistory' | 'breakCodes'>

export const useAgentStore = create<AgentStoreState>()((set, get) => ({
  ...initialState,
  
  /**
   * Apply the SERVER's view of this agent's status.
   *
   * `userState` is derived here rather than left to the toggle alone, because
   * the server changes it too: an agent is moved out of the available set by
   * ACW, by a supervisor force-pause, by the disconnect-grace sweeper, and by
   * taking a call. If the toggle were the only writer, the switch would keep
   * showing "Ready" for an agent the ACD had already stopped offering calls
   * to — the same class of lie the toggle itself used to tell, just in the
   * other direction.
   *
   * IDLE is the one status that means "available for routing"
   * (`AVAILABLE_STATUSES`), which is what Ready claims.
   */
  setStatus: (status, since) =>
    set({
      status,
      since: since ?? new Date().toISOString(),
      userState: status === 'IDLE' ? 'Waiting' : 'Idle',
    }),
  
  setCurrentCall: (currentCallId) => set({ currentCallId }),
  
  setUserStatus: (userStatus) => {
    const state = get()
    const now = new Date()
    
    // Calculate duration from last status change
    const lastEntry = state.sessionHistory[state.sessionHistory.length - 1]
    let duration = lastEntry
      ? Math.round((now.getTime() - lastEntry.time.getTime()) / 1000)
      : 0
    // If loading old records from DB, duration can be huge or negative;
    // clamp to 0 so accumulated times stay valid
    if (duration < 0 || duration > 86400) duration = 0
    
    const lastStatus = lastEntry ? lastEntry.text : undefined
    
    // Determine activity based on what changed
    let activity = 'Status Changed'
    if (userStatus.key === 'login') activity = 'Just Logged In'
    else if (lastEntry?.key === 'login' && userStatus.key === 'available') activity = 'Made Available'
    else if (userStatus.key === 'available') activity = 'Returned from Break'
    else if (!['login', 'available'].includes(userStatus.key)) activity = 'Went on Break'
    
    // Calculate cumulative times based on previous status.
    // If lastEntry is from a prior session (gap > 1h) reset counters.
    const isNewSession = lastEntry
      ? (Date.now() - lastEntry.time.getTime()) > 3_600_000
      : false
    let readyTime = isNewSession ? 0 : (lastEntry ? lastEntry.readyTime : 0)
    let availableTime = isNewSession ? 0 : (lastEntry ? lastEntry.availableTime : 0)
    let breakTime = isNewSession ? 0 : (lastEntry ? lastEntry.breakTime : 0)

    // Add the duration to the appropriate category based on the PREVIOUS status
    if (lastEntry && !isNewSession) {
      if (lastEntry.key === 'login') {
        readyTime += duration
      } else if (lastEntry.key === 'available') {
        availableTime += duration
      } else if (!['login', 'available', 'logout'].includes(lastEntry.key)) {
        breakTime += duration
      }
    }
    
    // Create session history entry
    const historyEntry: SessionHistory = {
      ...userStatus,
      time: now,
      activity,
      duration,
      lastStatus,
      readyTime,
      availableTime,
      breakTime,
    }
    
    console.log(`[Agent Status] Changed to: "${userStatus.text}" | Previous: "${lastStatus || 'none'}" | Duration: ${formatDuration(duration)} | Ready: ${formatDuration(readyTime)} | Available: ${formatDuration(availableTime)} | Break: ${formatDuration(breakTime)}`)
    
    // Update user state based on status
    let newUserState = state.userState
    let newLastUserState = state.lastUserState
    
    if (!['login', 'available'].includes(userStatus.key)) {
      // Going on break - save current state and set to Idle
      newLastUserState = state.userState
      newUserState = 'Idle'
    } else if (state.lastUserState) {
      // Returning from break - restore previous state
      newUserState = state.lastUserState
      newLastUserState = null
    }
    
    // Update break codes enabled/disabled state based on new status
    const updatedBreakCodes = state.breakCodes.map(code => {
      if (code.key === 'login') {
        return { ...code, disabled: userStatus.key !== 'login' }
      } else if (code.key === 'available') {
        return { ...code, disabled: userStatus.key === 'available' }
      } else {
        return { ...code, disabled: userStatus.key !== 'available' }
      }
    })
    
    set({
      userStatus,
      userState: newUserState,
      lastUserState: newLastUserState,
      sessionHistory: [...state.sessionHistory, historyEntry],
      breakCodes: updatedBreakCodes,
    })

    // Emit event so useAgentStatus can sync to backend
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('agent-status-changed', {
        detail: {
          userKey: userStatus.key,
          userStatus: userStatus.text,
          lastStatus: lastStatus ?? null,
          activity,
          duration,
          readyTime,
          availableTime,
          breakTime,
          userState: newUserState,
        },
      }))
    }
  },
  
  /**
   * Ready/Idle — the control that decides whether this agent receives calls.
   *
   * THE DEFECT
   * ----------
   * This was `console.log` + `set({ userState })`. Nothing else. The header
   * bar's own comment states that "Ready/Idle governs whether the dialer may
   * route a call to this agent", and it governed nothing: the toggle moved a
   * label while the agent's membership of the tenant's available set — the
   * set the ACD actually picks from — never changed.
   *
   * So an agent who switched themselves to Idle kept being offered calls, and
   * one who switched to Ready after a break was never added back. Both
   * failures are invisible from the UI, which shows the state the agent chose
   * rather than the state the server holds.
   *
   * WHAT IT DOES NOW
   * ----------------
   * Emits the same `agent:status` event the status dropdown uses, which the
   * gateway turns into an FSM transition plus `syncAvailableSet` — SADD on
   * IDLE, SREM on anything else (`presence/agent-presence.ts:207`). That set
   * is what `GlobalAgentRegistry` reads, so the toggle now moves the thing it
   * always claimed to move.
   *
   * NAMING TRAP: the UI's "Idle" means NOT ready, while the FSM's `IDLE`
   * means available. Ready -> `unpause` -> IDLE; Idle -> `pause` -> PAUSED.
   * They are inverted, which is exactly why this needs stating.
   *
   * The local `set` is applied optimistically so the switch responds
   * immediately; the server's `agent:status:ack` is authoritative and
   * `setStatus` reconciles it.
   */
  setUserState: (userState) => {
    set({ userState })
    try {
      emitAgentStatus(
        userState === 'Waiting' ? { event: 'unpause' } : { event: 'pause', reason: 'Idle' },
      )
    } catch (err) {
      // A socket that is not connected must not wedge the UI. The agent's
      // real state is whatever the server holds; a reconnect re-syncs it.
      console.warn('[Agent State] could not sync Ready/Idle to the server', err)
    }
  },
  
  setBreakCodes: (codes) => {
    const state = get()
    const currentStatus = state.userStatus.key

    const breakCodeOptions: UserStatusOption[] = codes.map(code => ({
      key: code.id,
      text: code.name,
      disabled: currentStatus !== 'available',
      alertTimer: code.alertTimer ?? null,
    }))

    console.log('[AgentStore] setBreakCodes - received codes:', codes)
    console.log('[AgentStore] setBreakCodes - mapped options:', breakCodeOptions)

    set({
      breakCodes: [
        { key: 'login', text: 'Just Logged In', disabled: currentStatus !== 'login', alertTimer: null },
        { key: 'available', text: 'Available', disabled: currentStatus === 'available', alertTimer: null },
        ...breakCodeOptions,
      ],
    })
  },

  loadSessionHistory: (records) => {
    // Strip cumulative times from DB-loaded records — they are from
    // previous sessions and must not bleed into the current session.
    const sanitized = records.map((r) => ({ ...r, readyTime: 0, availableTime: 0, breakTime: 0 }))
    set({ sessionHistory: sanitized })
  },
  
  logout: (forcefully = false) => {
    const state = get()
    const now = new Date()
    
    // Calculate total login duration
    const firstEntry = state.sessionHistory[0]
    const lastEntry = state.sessionHistory[state.sessionHistory.length - 1]
    const totalLoginDuration = firstEntry 
      ? Math.round((now.getTime() - firstEntry.time.getTime()) / 1000)
      : 0
    const lastDuration = lastEntry
      ? Math.round((now.getTime() - lastEntry.time.getTime()) / 1000)
      : 0
    
    // Add logout entry to history
    // Get last accumulated times from last entry
    const lastReadyTime = lastEntry ? lastEntry.readyTime : 0
    const lastAvailableTime = lastEntry ? lastEntry.availableTime : 0
    const lastBreakTime = lastEntry ? lastEntry.breakTime : 0
    
    // Add final duration to appropriate category
    const finalReadyTime = lastReadyTime
    let finalAvailableTime = lastAvailableTime
    let finalBreakTime = lastBreakTime
    
    if (lastEntry) {
      if (lastEntry.key === 'login' || lastEntry.key === 'available') {
        finalAvailableTime += lastDuration
      } else {
        finalBreakTime += lastDuration
      }
    }
    
    const logoutEntry: SessionHistory = {
      key: 'logout',
      text: 'Logout',
      time: now,
      activity: forcefully ? 'Logged Out Forcefully (Tab Closed)' : 'Logged Out Normally',
      duration: lastDuration,
      lastStatus: lastEntry?.text,
      readyTime: finalReadyTime,
      availableTime: finalAvailableTime,
      breakTime: finalBreakTime,
    }
    
    console.log(`[Agent Logout] ${forcefully ? 'Forcefully' : 'Normally'} | Total login: ${formatDuration(totalLoginDuration)}`)

    // Sync logout entry to backend
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('agent-status-changed', {
        detail: {
          userKey: logoutEntry.key,
          userStatus: logoutEntry.text,
          lastStatus: logoutEntry.lastStatus ?? null,
          activity: logoutEntry.activity,
          duration: logoutEntry.duration,
          readyTime: logoutEntry.readyTime,
          availableTime: logoutEntry.availableTime,
          breakTime: logoutEntry.breakTime,
          userState: state.userState,
        },
      }))
    }

    // Save history to localStorage for reporting before reset
    const fullHistory = [...state.sessionHistory, logoutEntry]
    localStorage.setItem('agentSessionHistory', JSON.stringify(fullHistory.map(h => ({
      ...h,
      time: h.time.toISOString(),
    }))))

    // Reset all state to initial
    set({ ...initialState })
  },
  
  reset: () => {
    localStorage.removeItem('agentSessionHistory')
    set({ ...initialState })
  },
}))

// Helper function for console logging
function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, totalSeconds) // clamp negatives to 0
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}
